/**
 * How the categories the server knows are shown.
 *
 * The list itself is no longer here: it is kept in the ledger and added to
 * through MCP, so it arrives from `GET /categories` together with the title to
 * show each one under. What stays is the colouring, which is a property of the
 * interface rather than of the ledger.
 *
 * The twelve the ledger is opened with keep the colours they always had —
 * grouped by what they are about, neighbours far apart in hue — and anything
 * added later is coloured from the same palette by its name, so a category
 * keeps one colour across reloads, charts and machines.
 */

import type { Category, CategoryFilter } from '../api/types';

/** Mantine palette names; neighbours in the chart are kept far apart in hue. */
export const CATEGORY_COLORS: Record<string, string> = {
  groceries: 'lime',
  dining: 'orange',
  transport: 'cyan',
  housing: 'indigo',
  health: 'pink',
  entertainment: 'grape',
  shopping: 'violet',
  education: 'blue',
  services: 'teal',
  travel: 'yellow',
  debt: 'red',
  salary: 'green',
};

/** What a category added later is coloured from; grey is left out — it means "none". */
const FALLBACK_COLORS: [string, ...string[]] = [
  'lime',
  'orange',
  'cyan',
  'indigo',
  'pink',
  'grape',
  'violet',
  'blue',
  'teal',
  'yellow',
  'red',
  'green',
];

/** Not a category, so it is grey and stands outside the palette above. */
export const UNCATEGORIZED_COLOR = 'gray';
export const UNCATEGORIZED_LABEL = 'Без категории';

/** The name a slice is shown under; `null` is the absence of a category. */
export function categoryLabel(category: Category | null, title: string | null): string {
  return category === null ? UNCATEGORIZED_LABEL : (title ?? category);
}

export function categoryColor(category: Category | null): string {
  if (category === null) {
    return UNCATEGORIZED_COLOR;
  }
  // The modulo keeps the index inside the palette; the second fallback is there
  // for the type checker, which cannot know that.
  return (
    CATEGORY_COLORS[category] ??
    FALLBACK_COLORS[hash(category) % FALLBACK_COLORS.length] ??
    FALLBACK_COLORS[0]
  );
}

/** What a slice of the chart filters the list by: the category, or their absence. */
export function categoryFilterOf(category: Category | null): CategoryFilter {
  return category ?? 'uncategorized';
}

/** Deterministic and tiny — the point is only that one name always picks one colour. */
function hash(name: string): number {
  let value = 0;
  for (const character of name) {
    value = (value * 31 + character.charCodeAt(0)) % 1_000_003;
  }
  return value;
}
